import { TableV3Filters } from 'common/table/components/TableV3';
import {
  conditionId,
  isSelection,
  withFreeIds
} from 'modules/candidate-profile/tables/filters/candidateFilterState';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import {
  CandidateFilterMatchMode,
  CandidateFilterState,
  CandidateFilterValueType,
  CandidateSelectionFilter,
  SourcingFilterField
} from 'modules/candidate-profile/types/CandidateSearchFilter';

type SearchFields =
  TableV3Filters<CandidateProfileTableColumns>['searchFields'];

// The searchable columns and the contract field each one searches.
export const FIELD_FOR_COLUMN: Record<string, string> = {
  name: 'fullName',
  currentCompany: 'currentCompany',
  currentJobTitle: 'currentJobTitle',
  email: 'email',
  phone: 'phone',
  atsCandidateId: 'atsCandidateId',
  originalSource: 'originalSource'
};

type SearchableType =
  | CandidateFilterValueType.TEXT
  | CandidateFilterValueType.TEXT_ARRAY;

const SEARCHABLE_TYPES: CandidateFilterValueType[] = [
  CandidateFilterValueType.TEXT,
  CandidateFilterValueType.TEXT_ARRAY
];

export const searchableCustomField = (
  column: string,
  availableFilters: SourcingFilterField[]
): SourcingFilterField | undefined =>
  availableFilters.find(
    (available) =>
      available.field === column &&
      Boolean(available.vectorDbKey) &&
      !available.allowedValues?.length &&
      SEARCHABLE_TYPES.includes(available.type)
  );

const COLUMN_FOR_FIELD: Record<string, string> = Object.fromEntries(
  Object.entries(FIELD_FOR_COLUMN).map(([column, field]) => [field, column])
);

export const isUniversalSearchColumn = (field: string): boolean =>
  field in COLUMN_FOR_FIELD;

export const searchColumnForField = (
  field: string,
  availableFilters: SourcingFilterField[] = []
): string | undefined =>
  COLUMN_FOR_FIELD[field] ??
  (searchableCustomField(field, availableFilters) ? field : undefined);

export type CandidateSearchEntry = {
  column: string;
  field: string;
  vectorDbKey?: string;
  type: SearchableType;
  value: string;
};

/** The column searches TableV3 is holding, in the order they were added. */
export const searchEntries = (
  searchFields: SearchFields,
  availableFilters: SourcingFilterField[] = []
): CandidateSearchEntry[] =>
  Object.entries(searchFields ?? {}).flatMap(([column, value]) => {
    const term = typeof value === 'string' ? value.trim() : '';
    if (!term) return [];

    const universalField = FIELD_FOR_COLUMN[column];
    if (universalField) {
      return [
        {
          column,
          field: universalField,
          type: CandidateFilterValueType.TEXT as SearchableType,
          value: term
        }
      ];
    }

    const custom = searchableCustomField(column, availableFilters);
    if (!custom) return [];

    return [
      {
        column,
        field: custom.field,
        ...(custom.vectorDbKey ? { vectorDbKey: custom.vectorDbKey } : {}),
        type: custom.type as SearchableType,
        value: term
      }
    ];
  });

// Several terms in one input, any of them: Popp derives several job titles
// from a query, and a recruiter typing them by hand expects the same.
export const searchTerms = (value: string): string[] =>
  value
    .split(',')
    .map((term) => term.trim())
    .filter(Boolean);

export const searchesToConditions = (
  searchFields: SearchFields,
  availableFilters: SourcingFilterField[] = []
): CandidateSelectionFilter[] =>
  searchEntries(searchFields, availableFilters).flatMap(
    ({ field, vectorDbKey, type, value }) => {
      // An input holding only commas still shows as a search.
      const terms = searchTerms(value);
      const values = terms.length ? terms : [value];

      return [
        {
          id: conditionId(field, CandidateFilterMatchMode.INCLUDE_ANY),
          field,
          ...(vectorDbKey ? { vectorDbKey } : {}),
          type,
          mode: CandidateFilterMatchMode.INCLUDE_ANY,
          values
        }
      ];
    }
  );

// A value with a comma stays a chip: the input would split it.
export const fitsSearchInput = (
  condition: CandidateFilterState
): condition is CandidateSelectionFilter =>
  isSelection(condition) &&
  condition.mode === CandidateFilterMatchMode.INCLUDE_ANY &&
  !condition.values.some((value) => value.includes(','));

export const searchInputConditionFor = (
  conditions: CandidateFilterState[],
  field: string
): CandidateSelectionFilter | undefined =>
  conditions.find(
    (condition): condition is CandidateSelectionFilter =>
      condition.field === field && fitsSearchInput(condition)
  );

const searchColumns = (
  availableFilters: SourcingFilterField[]
): { column: string; field: string }[] => [
  ...Object.entries(FIELD_FOR_COLUMN).map(([column, field]) => ({
    column,
    field
  })),
  ...availableFilters
    .filter((available) =>
      searchableCustomField(available.field, availableFilters)
    )
    .map((available) => ({ column: available.field, field: available.field }))
];

export const searchFieldsOf = (
  conditions: CandidateFilterState[],
  availableFilters: SourcingFilterField[]
): Record<string, string> =>
  Object.fromEntries(
    searchColumns(availableFilters).flatMap(({ column, field }) => {
      const held = searchInputConditionFor(conditions, field);
      return held ? [[column, held.values.join(', ')]] : [];
    })
  );

// A term replaces what its column held, under the same id, so an Or group
// naming it keeps naming it. A term no known field reads yet is `unreadable`.
export const applySearchTerms = (
  conditions: CandidateFilterState[],
  searchFields: SearchFields,
  availableFilters: SourcingFilterField[]
): {
  conditions: CandidateFilterState[];
  unreadable: Record<string, string>;
} => {
  const typed = searchesToConditions(searchFields, availableFilters);
  const typedByField = new Map(typed.map((term) => [term.field, term]));

  const kept = searchColumns(availableFilters).reduce((current, { field }) => {
    const held = searchInputConditionFor(current, field);
    const term = typedByField.get(field);
    if (!term) return current.filter((condition) => condition !== held);
    if (!held) return [...current, ...withFreeIds([term], current)];

    // Same object when unchanged: a turn in flight pairs chips by identity.
    const unchanged =
      term.values.length === held.values.length &&
      term.values.every((value, at) => value === held.values[at]);
    if (unchanged) return current;

    return current.map((condition) =>
      condition === held ? { ...held, values: term.values } : condition
    );
  }, conditions);

  const readColumns = new Set(
    searchEntries(searchFields, availableFilters).map((entry) => entry.column)
  );
  const unreadable = Object.fromEntries(
    Object.entries(searchFields ?? {}).filter(
      ([column, value]) =>
        typeof value === 'string' && value.trim() && !readColumns.has(column)
    )
  ) as Record<string, string>;

  return { conditions: kept, unreadable };
};
